/**
 * Man Coverage Rules
 * Defines CB reaction patterns for Man-to-Man coverage
 * Requirements: 7.2, 7.3
 */

import { CoverageDefinition, CoverageReaction, Position, DBLBReaction, FSReaction, CB2Reaction, DBLB2Reaction, SSReaction, LBReaction } from '../types';

/**
 * Starting position for CB1 in Man coverage
 * CB plays tighter to the WR (closer to line of scrimmage), ready to mirror movements
 * Coordinate system: x = lateral, y = downfield (lower y = further downfield/toward top)
 */
const CB1_MAN_START: Position = { x: 22, y: 75 };

/**
 * Starting position for DB/LB1 in Man coverage
 * Aligned with WR2 (x) and CB1 (y)
 */
const DBLB1_MAN_START: Position = { x: 30, y: 75 };

/**
 * Starting position for FS in Man coverage
 * Deep left center - responsible for deep half on left side
 */
const FS_MAN_START: Position = { x: 35, y: 50 };

/**
 * Starting position for CB2 in Man coverage (mirrors CB1 on right side)
 */
const CB2_MAN_START: Position = { x: 78, y: 75 };

/**
 * Starting position for DB/LB2 in Man coverage (mirrors DB/LB1 on right side)
 */
const DBLB2_MAN_START: Position = { x: 70, y: 75 };

/**
 * Starting position for SS in Man coverage (mirrors FS on right side)
 */
const SS_MAN_START: Position = { x: 65, y: 50 };

/**
 * Starting position for LB in Man coverage
 * LB is responsible for covering the RB
 */
const LB_MAN_START: Position = { x: 50, y: 70 };

/**
 * Man coverage reaction rules
 * In Man coverage, the CB mirrors the WR's movements closely
 * with minimal delay in reactions
 * 
 * Coordinate system (vertical orientation):
 * - x: lateral position (0=left sideline, 100=right sideline)
 * - y: downfield position (100=line of scrimmage at bottom, 0=endzone at top)
 */
const manReactions: CoverageReaction[] = [
  {
    // Slant: CB backpedals, then breaks with WR when he cuts inside
    routeTrigger: 'slant',
    wrPositionTrigger: { x: 20, y: 70 },
    cbResponse: [
      { x: 22, y: 65 },  // backpedal
      { x: 73, y: 38 },  // break with WR on slant
    ],
    responseDelay: 0,
  },
  {
    // Out: Short 5-yard out - CB follows to sideline
    routeTrigger: 'out',
    wrPositionTrigger: { x: 20, y: 70 },
    cbResponse: [
      { x: 22, y: 68 },  // backpedal
      { x: 7, y: 68 },   // break to sideline with WR
    ],
    responseDelay: 0,
  },
  {
    // Deep Out: CB backpedals, then breaks to sideline with WR
    routeTrigger: 'deepOut',
    wrPositionTrigger: { x: 20, y: 45 },
    cbResponse: [
      { x: 22, y: 42 },  // backpedal staying over WR
      { x: 7, y: 42 },   // break to sideline with WR
    ],
    responseDelay: 0,
  },
  {
    // Deep In: CB backpedals, then breaks inside with WR
    routeTrigger: 'deepIn',
    wrPositionTrigger: { x: 20, y: 45 },
    cbResponse: [
      { x: 22, y: 42 },  // backpedal staying over WR
      { x: 58, y: 42 },  // break inside with WR
    ],
    responseDelay: 0,
  },
  {
    // Comeback: CB runs with WR deep, then closes when WR comes back
    routeTrigger: 'comeback',
    wrPositionTrigger: { x: 20, y: 25 },
    cbResponse: [
      { x: 22, y: 22 },  // run deep with WR
      { x: 17, y: 38 },  // close on comeback
    ],
    responseDelay: 0,
  },
  {
    // Go: CB turns and runs stride for stride with WR
    routeTrigger: 'go',
    wrPositionTrigger: { x: 20, y: 60 },
    cbResponse: [
      { x: 22, y: 8 },   // run deep with WR
    ],
    responseDelay: 0,
  },
  {
    // Post: 10-yard inside route - CB backpedals then breaks inside with WR
    routeTrigger: 'post',
    wrPositionTrigger: { x: 20, y: 55 },
    cbResponse: [
      { x: 22, y: 52 },  // backpedal staying over WR
      { x: 48, y: 18 },  // break inside with WR on post
    ],
    responseDelay: 0,
  },
  {
    // Corner: 10-yard outside route - CB backpedals then breaks outside with WR
    routeTrigger: 'corner',
    wrPositionTrigger: { x: 20, y: 55 },
    cbResponse: [
      { x: 22, y: 52 },  // backpedal staying over WR
      { x: 7, y: 18 },   // break outside with WR on corner
    ],
    responseDelay: 0,
  },
  {
    // Drag: Shallow cross - CB trails WR across the field
    routeTrigger: 'drag',
    wrPositionTrigger: { x: 20, y: 80 },
    cbResponse: [
      { x: 22, y: 78 },  // read release
      { x: 68, y: 73 },  // trail WR across the field
    ],
    responseDelay: 0,
  },
  {
    // Hitch: 5-yard stop - CB backpedals then breaks on the ball
    routeTrigger: 'hitch',
    wrPositionTrigger: { x: 20, y: 70 },
    cbResponse: [
      { x: 22, y: 68 },  // backpedal
      { x: 24, y: 71 },  // close on hitch
    ],
    responseDelay: 0,
  },
  {
    // Curl: 10-yard stop - CB backpedals then closes on the turn
    routeTrigger: 'curl',
    wrPositionTrigger: { x: 20, y: 55 },
    cbResponse: [
      { x: 22, y: 52 },  // backpedal staying over WR
      { x: 24, y: 58 },  // close on curl
    ],
    responseDelay: 0,
  },
];

/**
 * DB/LB1 reaction rules for Man coverage
 * In Man coverage, DB/LB1 covers WR2 man-to-man
 * WR2 runs offset from WR1 (+10x, +5y)
 */
const manDBLB1Reactions: DBLBReaction[] = [
  {
    // Slant: Follow WR2 on slant
    routeTrigger: 'slant',
    response: [
      { x: 32, y: 65 },  // backpedal
      { x: 83, y: 43 },  // break with WR2 on slant
    ],
  },
  {
    // Out: Short 5-yard out - follow WR2 to sideline
    routeTrigger: 'out',
    response: [
      { x: 32, y: 73 },  // backpedal
      { x: 17, y: 73 },  // break to sideline with WR2
    ],
  },
  {
    // Deep Out: Backpedal then break to sideline with WR2
    routeTrigger: 'deepOut',
    response: [
      { x: 32, y: 50 },  // backpedal staying over WR2
      { x: 15, y: 50 },  // break to sideline with WR2
    ],
  },
  {
    // Deep In: Backpedal then break inside with WR2
    routeTrigger: 'deepIn',
    response: [
      { x: 32, y: 50 },  // backpedal staying over WR2
      { x: 70, y: 50 },  // break inside with WR2
    ],
  },
  {
    // Comeback: Run deep with WR2, close on comeback
    routeTrigger: 'comeback',
    response: [
      { x: 32, y: 32 },  // run deep with WR2
      { x: 25, y: 45 },  // close on comeback
    ],
  },
  {
    // Go: Run stride for stride with WR2
    routeTrigger: 'go',
    response: [
      { x: 32, y: 10 },  // run deep with WR2
    ],
  },
  {
    // Post: 10-yard inside route - follow WR2 on post
    routeTrigger: 'post',
    response: [
      { x: 32, y: 52 },  // backpedal staying over WR2
      { x: 60, y: 18 },  // break inside with WR2 on post
    ],
  },
  {
    // Corner: 10-yard outside route - follow WR2 on corner
    routeTrigger: 'corner',
    response: [
      { x: 32, y: 52 },  // backpedal staying over WR2
      { x: 15, y: 18 },  // break outside with WR2 on corner
    ],
  },
  {
    // Drag: Shallow cross - trail WR2 across the field
    routeTrigger: 'drag',
    response: [
      { x: 32, y: 78 },  // read release
      { x: 78, y: 73 },  // trail WR2 across
    ],
  },
  {
    // Hitch: 5-yard stop - close on WR2
    routeTrigger: 'hitch',
    response: [
      { x: 32, y: 73 },  // backpedal
      { x: 34, y: 76 },  // close on hitch
    ],
  },
  {
    // Curl: 10-yard stop - close on WR2
    routeTrigger: 'curl',
    response: [
      { x: 32, y: 52 },  // backpedal
      { x: 34, y: 58 },  // close on curl
    ],
  },
];

/**
 * FS reaction rules for Man coverage
 * In Man coverage, FS drops back in zone coverage to help over the top on deep routes
 */
const manFSReactions: FSReaction[] = [
  {
    // Short routes — moderate drop, stay in deep half
    routeTrigger: 'slant',
    response: [{ x: 35, y: 35 }, { x: 35, y: 22 }],
  },
  {
    routeTrigger: 'out',
    response: [{ x: 35, y: 35 }, { x: 35, y: 22 }],
  },
  {
    // Deep out — drop and shade toward sideline
    routeTrigger: 'deepOut',
    response: [{ x: 32, y: 30 }, { x: 18, y: 15 }],
  },
  {
    // Deep in — drop and shade inside
    routeTrigger: 'deepIn',
    response: [{ x: 38, y: 30 }, { x: 50, y: 15 }],
  },
  {
    // Comeback — drop deep, then close when WR comes back
    routeTrigger: 'comeback',
    response: [{ x: 30, y: 25 }, { x: 22, y: 12 }],
  },
  {
    // Go — turn and run deep, stay over top
    routeTrigger: 'go',
    response: [{ x: 32, y: 25 }, { x: 25, y: 10 }, { x: 22, y: 5 }],
  },
  {
    // Post — drop deep, shade toward middle
    routeTrigger: 'post',
    response: [{ x: 38, y: 28 }, { x: 45, y: 15 }, { x: 48, y: 8 }],
  },
  {
    // Corner — drop deep, shade toward sideline
    routeTrigger: 'corner',
    response: [{ x: 30, y: 28 }, { x: 18, y: 15 }, { x: 10, y: 8 }],
  },
  {
    // Multiple deep threats — stay centered in deep half, don't commit
    routeTrigger: 'deep_help',
    response: [{ x: 30, y: 28 }, { x: 28, y: 15 }, { x: 25, y: 8 }],
  },
  {
    // Drag — short route, moderate drop
    routeTrigger: 'drag',
    response: [{ x: 35, y: 35 }, { x: 35, y: 22 }],
  },
  {
    // Hitch — short route, moderate drop
    routeTrigger: 'hitch',
    response: [{ x: 35, y: 35 }, { x: 35, y: 22 }],
  },
  {
    // Curl — 10yd stop, drop and shade
    routeTrigger: 'curl',
    response: [{ x: 32, y: 30 }, { x: 28, y: 18 }],
  },
];

/**
 * CB2 reaction rules for Man coverage (mirrors CB1 for WR4 on right side)
 * All x coordinates are mirrored (100 - x)
 */
const manCB2Reactions: CB2Reaction[] = [
  {
    // Slant: CB2 backpedals, then breaks with WR4 when he cuts inside (mirrored)
    routeTrigger: 'slant',
    wrPositionTrigger: { x: 80, y: 70 },
    cbResponse: [
      { x: 78, y: 65 },  // backpedal
      { x: 27, y: 38 },  // break with WR4 on slant (mirrored)
    ],
    responseDelay: 0,
  },
  {
    // Out: Short 5-yard out - CB2 follows to sideline (mirrored)
    routeTrigger: 'out',
    wrPositionTrigger: { x: 80, y: 70 },
    cbResponse: [
      { x: 78, y: 68 },  // backpedal
      { x: 93, y: 68 },  // break to sideline with WR4 (mirrored)
    ],
    responseDelay: 0,
  },
  {
    // Deep Out: CB2 backpedals, then breaks to sideline with WR4 (mirrored)
    routeTrigger: 'deepOut',
    wrPositionTrigger: { x: 80, y: 45 },
    cbResponse: [
      { x: 78, y: 42 },  // backpedal staying over WR4
      { x: 93, y: 42 },  // break to sideline with WR4 (mirrored)
    ],
    responseDelay: 0,
  },
  {
    // Deep In: CB2 backpedals, then breaks inside with WR4 (mirrored)
    routeTrigger: 'deepIn',
    wrPositionTrigger: { x: 80, y: 45 },
    cbResponse: [
      { x: 78, y: 42 },  // backpedal staying over WR4
      { x: 42, y: 42 },  // break inside with WR4 (mirrored)
    ],
    responseDelay: 0,
  },
  {
    // Comeback: CB2 runs with WR4 deep, then closes when WR4 comes back (mirrored)
    routeTrigger: 'comeback',
    wrPositionTrigger: { x: 80, y: 25 },
    cbResponse: [
      { x: 78, y: 22 },  // run deep with WR4
      { x: 83, y: 38 },  // close on comeback (mirrored)
    ],
    responseDelay: 0,
  },
  {
    // Go: CB2 turns and runs stride for stride with WR4 (mirrored)
    routeTrigger: 'go',
    wrPositionTrigger: { x: 80, y: 60 },
    cbResponse: [
      { x: 78, y: 8 },   // run deep with WR4
    ],
    responseDelay: 0,
  },
  {
    // Post: 10-yard inside route - CB2 backpedals then breaks inside with WR4 (mirrored)
    routeTrigger: 'post',
    wrPositionTrigger: { x: 80, y: 55 },
    cbResponse: [
      { x: 78, y: 52 },  // backpedal staying over WR4
      { x: 52, y: 18 },  // break inside with WR4 on post (mirrored)
    ],
    responseDelay: 0,
  },
  {
    // Corner: 10-yard outside route - CB2 backpedals then breaks outside with WR4 (mirrored)
    routeTrigger: 'corner',
    wrPositionTrigger: { x: 80, y: 55 },
    cbResponse: [
      { x: 78, y: 52 },  // backpedal staying over WR4
      { x: 93, y: 18 },  // break outside with WR4 on corner (mirrored)
    ],
    responseDelay: 0,
  },
  {
    // Drag: Shallow cross - CB2 trails WR4 across (mirrored)
    routeTrigger: 'drag',
    wrPositionTrigger: { x: 80, y: 80 },
    cbResponse: [
      { x: 78, y: 78 },  // read release
      { x: 32, y: 73 },  // trail WR4 across (mirrored)
    ],
    responseDelay: 0,
  },
  {
    // Hitch: 5-yard stop - CB2 closes on WR4 (mirrored)
    routeTrigger: 'hitch',
    wrPositionTrigger: { x: 80, y: 70 },
    cbResponse: [
      { x: 78, y: 68 },  // backpedal
      { x: 76, y: 71 },  // close on hitch (mirrored)
    ],
    responseDelay: 0,
  },
  {
    // Curl: 10-yard stop - CB2 closes on WR4 (mirrored)
    routeTrigger: 'curl',
    wrPositionTrigger: { x: 80, y: 55 },
    cbResponse: [
      { x: 78, y: 52 },  // backpedal
      { x: 76, y: 58 },  // close on curl (mirrored)
    ],
    responseDelay: 0,
  },
];

/**
 * DB/LB2 reaction rules for Man coverage (mirrors DB/LB1 for WR3 on right side)
 * WR3 runs mirrored offset from WR4 (100 - x - 10, +5y)
 */
const manDBLB2Reactions: DBLB2Reaction[] = [
  {
    // Slant: Follow WR3 on slant (mirrored)
    routeTrigger: 'slant',
    response: [
      { x: 68, y: 65 },  // backpedal
      { x: 17, y: 43 },  // break with WR3 on slant (mirrored)
    ],
  },
  {
    // Out: Short 5-yard out - follow WR3 to sideline (mirrored)
    routeTrigger: 'out',
    response: [
      { x: 68, y: 73 },  // backpedal
      { x: 83, y: 73 },  // break to sideline with WR3 (mirrored)
    ],
  },
  {
    // Deep Out: Backpedal then break to sideline with WR3 (mirrored)
    routeTrigger: 'deepOut',
    response: [
      { x: 68, y: 50 },  // backpedal staying over WR3
      { x: 85, y: 50 },  // break to sideline with WR3 (mirrored)
    ],
  },
  {
    // Deep In: Backpedal then break inside with WR3 (mirrored)
    routeTrigger: 'deepIn',
    response: [
      { x: 68, y: 50 },  // backpedal staying over WR3
      { x: 30, y: 50 },  // break inside with WR3 (mirrored)
    ],
  },
  {
    // Comeback: Run deep with WR3, close on comeback (mirrored)
    routeTrigger: 'comeback',
    response: [
      { x: 68, y: 32 },  // run deep with WR3
      { x: 75, y: 45 },  // close on comeback (mirrored)
    ],
  },
  {
    // Go: Run stride for stride with WR3 (mirrored)
    routeTrigger: 'go',
    response: [
      { x: 68, y: 10 },  // run deep with WR3
    ],
  },
  {
    // Post: 10-yard inside route - follow WR3 on post (mirrored)
    routeTrigger: 'post',
    response: [
      { x: 68, y: 52 },  // backpedal staying over WR3
      { x: 40, y: 18 },  // break inside with WR3 on post (mirrored)
    ],
  },
  {
    // Corner: 10-yard outside route - follow WR3 on corner (mirrored)
    routeTrigger: 'corner',
    response: [
      { x: 68, y: 52 },  // backpedal staying over WR3
      { x: 85, y: 18 },  // break outside with WR3 on corner (mirrored)
    ],
  },
  {
    // Drag: Shallow cross - trail WR3 across (mirrored)
    routeTrigger: 'drag',
    response: [
      { x: 68, y: 78 },  // read release
      { x: 22, y: 73 },  // trail WR3 across (mirrored)
    ],
  },
  {
    // Hitch: 5-yard stop - close on WR3 (mirrored)
    routeTrigger: 'hitch',
    response: [
      { x: 68, y: 73 },  // backpedal
      { x: 66, y: 76 },  // close on hitch (mirrored)
    ],
  },
  {
    // Curl: 10-yard stop - close on WR3 (mirrored)
    routeTrigger: 'curl',
    response: [
      { x: 68, y: 52 },  // backpedal
      { x: 66, y: 58 },  // close on curl (mirrored)
    ],
  },
];

/**
 * SS reaction rules for Man coverage (mirrors FS for right side)
 * In Man coverage, SS drops back in zone coverage to help over the top on deep routes
 */
const manSSReactions: SSReaction[] = [
  {
    // Short routes — moderate drop, stay in deep half
    routeTrigger: 'slant',
    response: [{ x: 65, y: 35 }, { x: 65, y: 22 }],
  },
  {
    routeTrigger: 'out',
    response: [{ x: 65, y: 35 }, { x: 65, y: 22 }],
  },
  {
    // Deep out — drop and shade toward sideline
    routeTrigger: 'deepOut',
    response: [{ x: 68, y: 30 }, { x: 82, y: 15 }],
  },
  {
    // Deep in — drop and shade inside
    routeTrigger: 'deepIn',
    response: [{ x: 62, y: 30 }, { x: 50, y: 15 }],
  },
  {
    // Comeback — drop deep, stay over top
    routeTrigger: 'comeback',
    response: [{ x: 70, y: 25 }, { x: 78, y: 12 }],
  },
  {
    // Go — turn and run deep, stay over top
    routeTrigger: 'go',
    response: [{ x: 68, y: 25 }, { x: 75, y: 10 }, { x: 78, y: 5 }],
  },
  {
    // Post — drop deep, shade toward middle
    routeTrigger: 'post',
    response: [{ x: 62, y: 28 }, { x: 55, y: 15 }, { x: 52, y: 8 }],
  },
  {
    // Corner — drop deep, shade toward sideline
    routeTrigger: 'corner',
    response: [{ x: 70, y: 28 }, { x: 82, y: 15 }, { x: 90, y: 8 }],
  },
  {
    // Multiple deep threats — stay centered in deep half, don't commit
    routeTrigger: 'deep_help',
    response: [{ x: 70, y: 28 }, { x: 72, y: 15 }, { x: 75, y: 8 }],
  },
  {
    // Drag — short route, moderate drop
    routeTrigger: 'drag',
    response: [{ x: 65, y: 35 }, { x: 65, y: 22 }],
  },
  {
    // Hitch — short route, moderate drop
    routeTrigger: 'hitch',
    response: [{ x: 65, y: 35 }, { x: 65, y: 22 }],
  },
  {
    // Curl — 10yd stop, drop and shade
    routeTrigger: 'curl',
    response: [{ x: 68, y: 30 }, { x: 72, y: 18 }],
  },
];

/**
 * LB reaction rules for Man coverage
 * In Man coverage, LB covers the RB man-to-man on all routes
 * RB starts at (55, 92)
 */
const manLBReactions: LBReaction[] = [
  {
    // Out: RB runs 5 yards upfield then breaks out - LB follows
    routeTrigger: 'out',
    response: [
      { x: 57, y: 77 },  // follow RB upfield
      { x: 85, y: 77 },  // break out with RB
    ],
  },
  {
    // Swing: RB swings to the flat - LB follows
    routeTrigger: 'swing',
    response: [
      { x: 75, y: 82 },  // follow RB to flat
      { x: 90, y: 78 },  // continue with RB
    ],
  },
  {
    // Curl: RB runs 5 yards upfield then turns - LB follows
    routeTrigger: 'curl',
    response: [
      { x: 55, y: 77 },  // follow RB upfield
      { x: 54, y: 80 },  // close on curl
    ],
  },
  {
    // Wheel: RB swings to flat then turns upfield - LB follows
    routeTrigger: 'wheel',
    response: [
      { x: 75, y: 82 },  // follow RB to flat
      { x: 85, y: 78 },  // continue with RB
      { x: 85, y: 12 },  // run deep with RB
    ],
  },
];

/**
 * Complete Man coverage definition
 */
export const manDefinition: CoverageDefinition = {
  id: 'man',
  name: 'Man',
  description: 'Man-to-man coverage with tight WR tracking',
  initialPosition: CB1_MAN_START,
  reactionRules: manReactions,
  dblb1InitialPosition: DBLB1_MAN_START,
  dblb1ReactionRules: manDBLB1Reactions,
  fsInitialPosition: FS_MAN_START,
  fsReactionRules: manFSReactions,
  cb2InitialPosition: CB2_MAN_START,
  cb2ReactionRules: manCB2Reactions,
  dblb2InitialPosition: DBLB2_MAN_START,
  dblb2ReactionRules: manDBLB2Reactions,
  ssInitialPosition: SS_MAN_START,
  ssReactionRules: manSSReactions,
  lbInitialPosition: LB_MAN_START,
  lbReactionRules: manLBReactions,
};

/**
 * Get the initial CB1 position for Man coverage
 */
export function getManStartPosition(): Position {
  return { ...CB1_MAN_START };
}

/**
 * Get the initial DB/LB1 position for Man coverage
 */
export function getManDBLB1StartPosition(): Position {
  return { ...DBLB1_MAN_START };
}

/**
 * Get the initial FS position for Man coverage
 */
export function getManFSStartPosition(): Position {
  return { ...FS_MAN_START };
}

/**
 * Get reaction rules for a specific route in Man coverage
 */
export function getManReaction(routeTrigger: string): CoverageReaction | undefined {
  return manReactions.find((r) => r.routeTrigger === routeTrigger);
}

/**
 * Get DB/LB1 reaction rules for a specific route in Man coverage
 */
export function getManDBLB1Reaction(routeTrigger: string): DBLBReaction | undefined {
  return manDBLB1Reactions.find((r) => r.routeTrigger === routeTrigger);
}

/**
 * Get FS reaction rules for a specific route in Man coverage
 */
export function getManFSReaction(routeTrigger: string): FSReaction | undefined {
  return manFSReactions.find((r) => r.routeTrigger === routeTrigger);
}

/**
 * Get all Man coverage reaction rules
 */
export function getManReactions(): CoverageReaction[] {
  return [...manReactions];
}

/**
 * Get the complete Man coverage definition
 */
export function getManDefinition(): CoverageDefinition {
  return { ...manDefinition };
}

export { CB1_MAN_START, DBLB1_MAN_START, FS_MAN_START, CB2_MAN_START, DBLB2_MAN_START, SS_MAN_START, LB_MAN_START, manReactions, manDBLB1Reactions, manFSReactions, manCB2Reactions, manDBLB2Reactions, manSSReactions, manLBReactions };
